import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import "./ScheduleView.scss";
import CustomToolTip from "../../../CommonComponents/CustomToolTip";
import { useDispatch, useSelector } from "react-redux";
import UnifiedControlPlaneService from "../../UnifiedControlPlane.service";
import { getTimeUTCWithNoMs } from "../Utils/HelperFunctions";
import {
  setPreviewScheduleId,
  setShowReviewSchedulePopup,
  setShowGenerateScheduleConfig,
} from "../../../../store/UnifiedControlPlaneSlice";
import { ReactComponent as GenerateSchedulelistIcon } from "../../assets/raw/GenerateSchedulelist.svg";
import { CircularProgress } from "@mui/material";
import { EmptyListIcon, SortColumnIcon } from "../../assets/icons/Icons";
import CustomFilter from "../../../CommonComponents/custom_filter/CustomFilter";
import { FilterSvg } from "../../../CommonComponents/svgs/Svgs";
import { getFilterOption, handleApplyFilters } from "./utils";

const ScheduleListView = ({ mission_id }) => {
  const dispatch = useDispatch();
  const schedules = useSelector((state) => state.unifiedControlPlane.schedules);
  const fetchingAllSchedules = useSelector(
    (state) => state.unifiedControlPlane.fetchingAllSchedules
  );
  const [menuOpen, setMenuOpen] = useState(null);
  const menuRef = useRef(null);

  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });
  const [scheduleData, setScheduleData] = useState([]);
  const [filterApplied, setFilterApplied] = useState(false);
  const [filteredScheduleData, setFilteredScheduleData] = useState([])
  const filterOption = useMemo(() => getFilterOption(filteredScheduleData), [filteredScheduleData])
  const [allFilters, setAllFilters] = useState({
    advanceFilters: [
      {
        operator: '',
        properties: '',
        value: '',
      },
    ],
    searchFilter: '',
  });

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (schedules) {
      setScheduleData(schedules)
      setFilteredScheduleData(filterApplied
        ? prev => prev.filter((item) => schedules.some((schedule) => schedule.scheduleId === item.scheduleId))
        : schedules)
    }
  }, [schedules, filterApplied])

  const threeDotMenuClick = (e, index) => {
    e.stopPropagation();
    setMenuOpen(menuOpen === index ? null : index); // Toggle menu visibility
  };

  const closeScheduleReview = () => {
    dispatch(setShowReviewSchedulePopup(""));
  };

  const showScheduleDetails = (schedule) => {
    dispatch(setPreviewScheduleId(schedule?.scheduleId));
    dispatch(
      UnifiedControlPlaneService.getOrderAssignments(
        mission_id,
        schedule?.scheduleId
      )
    );
    dispatch(setShowReviewSchedulePopup("single-schedule"));
  };

  const getClassNameByStatus = (status) => {
    if (status === "Generating") return "schedule_state_green";
    if (status === "Experimental") return "schedule_state_blue";
    if (status === "Draft") return "schedule_state_blue";
    if (status === "Empty") return "schedule_state_red";
    if (status === "Current") return "schedule_state_green";
    if (status === "Superseded") return "schedule_state_orange";
    if (status === "Rejected") return "schedule_state_red";
    if (status === "Expired") return "schedule_state_grey";
    if (status === "Archived") return "schedule_state_grey";
    return "";
  };

  const scheduleUpdateSuccessCallback = () => {
    dispatch(UnifiedControlPlaneService.getAllSchedules(mission_id));
    dispatch(UnifiedControlPlaneService.getOrders());
  };

  const updateSchedule = (event, schedule, action) => {
    event.stopPropagation();
    setMenuOpen(null);

    if (action === "Delete") {
      setScheduleData(prev => prev.filter(item => item.scheduleId !== schedule.scheduleId));
      setFilteredScheduleData(prev => prev.filter(item => item.scheduleId !== schedule.scheduleId));

      dispatch(
        UnifiedControlPlaneService.deleteScheduleById(
          mission_id,
          action,
          schedule,
          () => { },
          (error) => {
            // rollback if API fails
            setScheduleData(prev => [...prev, schedule]);
            setFilteredScheduleData(prev => [...prev, schedule]);
          }
        )
      );
    } else {
      dispatch(
        UnifiedControlPlaneService.updateSchedules(
          mission_id,
          action,
          schedule,
          scheduleUpdateSuccessCallback
        )
      );
    }
  };

  const handleClickGenerate = () => {
    closeScheduleReview();
    dispatch(setShowGenerateScheduleConfig(true));
  };

  const getNestedValue = (obj, path) => {
    return path.split(".").reduce((acc, part) => acc && acc[part], obj);
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }

    const sortedData = [...scheduleData].sort((a, b) => {
      const aValue = getNestedValue(a, key);
      const bValue = getNestedValue(b, key);

      if (aValue < bValue) return direction === "asc" ? -1 : 1;
      if (aValue > bValue) return direction === "asc" ? 1 : -1;
      return 0;
    });

    setScheduleData(sortedData);
    setSortConfig({ key, direction });
  }

  const handleScheduleListFilters = (scheduleData, filters) => {
    const updatedSchedules = handleApplyFilters(scheduleData, filters);
    setFilteredScheduleData(updatedSchedules);
    setFilterApplied(scheduleData.length !== updatedSchedules.length)
  }

  return (
    <div className={`list-schedule ${fetchingAllSchedules ? "loading-schedule" : ""}`}>
      <div className="current-schedule-header">
        <div className="current-schedule-title">
          <div className="current-schedule-title-icon">
            <GenerateSchedulelistIcon />
          </div>
          <p className="current-schedule-title-text">Schedules</p>
        </div>
        <div className="current-schedule-orders-summary">
          <div
            className="current-schedule-close-action"
            onClick={closeScheduleReview}
          >
            <img
              className="CloseIcon"
              src={require("../../assets/raw/CloseIconWhite.svg").default}
              alt="close-btn"
            />
          </div>
        </div>
      </div>
      {fetchingAllSchedules ? (
        <div className="loading-schedule-msg">
          <CircularProgress color="inherit" />
        </div>
      ) : scheduleData && scheduleData?.length > 0 ? (
        <Fragment>
          <div className="schedule_list_filter_section">
            <CustomFilter
              alignment="right"
              search={{
                applyFilter: (filters) => {
                  handleScheduleListFilters(scheduleData, filters)
                }
              }}
              advanceFilter={{
                filterIcon: <FilterSvg />,
                filterList: filterOption,
                applyFilter: (filters) => {
                  handleScheduleListFilters(scheduleData, filters)
                }
              }}
              allFilters={allFilters}
              setAllFilters={setAllFilters}
              filterCase={'ucp'}
              boundingScreen={'list-schedule'}
            />
          </div>
          <div className="schedule_list_header_section">
            <div className="schedule_list_header">
              <div className="schedule_label_width col-header">
                Schedules
                <span
                  className="table-sort"
                  onClick={() => handleSort("label")}
                >
                  <SortColumnIcon />
                </span>
              </div>
              <div className="schedule_priority_section_width col-header">
                Orders
              </div>
              <div className="schedule_date_width col-header">
                Start Time
                <span
                  className="table-sort"
                  onClick={() => handleSort("createdAt")}
                >
                  <SortColumnIcon />
                </span>
              </div>
              <div className="schedule_state_div_width">Status</div>
              <div className="schedule_three_dot_menu_width"></div>
            </div>
          </div>
          <div className="left__schedule__panel__container schedule_list_section">
            <div className="right__schedule__panel__container__table__body schedule__details__section schedule_list_no_gap">
              {filteredScheduleData?.map((schedule, index) => {
                return (
                  <Fragment key={schedule?.scheduleId}>
                    {schedule?.state && (
                      <div
                        className={`schedule__details__container schedule_list_item ${schedule?.state === "Current" ? "active" : ""
                          }`}
                        onClick={() => {
                          showScheduleDetails(schedule);
                        }}
                      >
                        <div className="details__container schedule_label schedule_label_width">
                          <div className="details__container__name">
                            <CustomToolTip
                              title={schedule?.label}
                              placement="top-start"
                            >
                              {schedule?.label}
                            </CustomToolTip>
                          </div>
                        </div>

                        <div className="schedule_priority_section schedule_priority_section_width">
                          {schedule?.scheduleSummary?.asapOrderCount && (
                            <div className="schedule_priority">
                              <span>
                                {
                                  <img
                                    className="priority_icons high_asap_priority_icon"
                                    src={
                                      require("../../assets/raw/Asap.svg")
                                        .default
                                    }
                                    alt=""
                                  />
                                }
                                {schedule?.scheduleSummary?.asapOrderCount}
                              </span>
                            </div>
                          )}
                          {schedule?.scheduleSummary?.highOrderCount && (
                            <div className="schedule_priority">
                              <span>
                                {
                                  <img
                                    className="priority_icons high_asap_priority_icon"
                                    src={
                                      require("../../assets/raw/High.svg")
                                        .default
                                    }
                                    alt=""
                                  />
                                }
                                {schedule?.scheduleSummary?.highOrderCount}
                              </span>
                            </div>
                          )}
                          {schedule?.scheduleSummary?.normalOrderCount && (
                            <div className="schedule_priority">
                              <span>
                                {
                                  <img
                                    className="priority_icons"
                                    src={
                                      require("../../assets/raw/Normal.svg")
                                        .default
                                    }
                                    alt=""
                                  />
                                }
                                {schedule?.scheduleSummary?.normalOrderCount}
                              </span>
                            </div>
                          )}
                          {Object.keys(schedule?.scheduleSummary).length ===
                            0 && (
                              <div className="schedule_priority">
                                <span>N/A</span>
                              </div>
                            )}
                        </div>

                        <div
                          className={`schedule_date schedule_date_width schedule_sub_text`}
                        >
                          <CustomToolTip
                            title={schedule?.createdAt}
                            placement="top-start"
                          >
                            {getTimeUTCWithNoMs(schedule?.createdAt)}
                          </CustomToolTip>
                        </div>

                        <div className="schedule_state_div schedule_state_div_width">
                          <div
                            className={`schedule_state ${getClassNameByStatus(
                              schedule?.state
                            )}`}
                          >
                            <p>{schedule?.state}</p>
                          </div>
                        </div>

                        <div className="schedule_three_dot_menu schedule_three_dot_menu_width">
                          {schedule?.state === "Draft" ||
                            schedule?.state === "Experimental" ||
                            schedule?.state === "Rejected" ||
                            schedule?.state === "Expired" ||
                            schedule?.state === "Generating" ||
                            schedule?.state === "Empty" ? (
                            <div
                              className="orderThreeDotMenu mouse-cursor-pointer "
                              onClick={(e) => {
                                threeDotMenuClick(e, index);
                              }}
                            >
                              <img
                                src={
                                  require("../../assets/raw/ThreeDotIcon.svg")
                                    .default
                                }
                                alt=""
                              />
                              {menuOpen === index &&
                                schedule?.state === "Draft" && (
                                  <div
                                    className="three-dot-menu-popup"
                                    ref={menuRef}
                                  >
                                    <ul>
                                      <li
                                        onClick={(e) =>
                                          updateSchedule(e, schedule, "Approve")
                                        }
                                      >
                                        Approve
                                      </li>
                                      <li
                                        onClick={(e) =>
                                          updateSchedule(e, schedule, "Reject")
                                        }
                                      >
                                        Reject
                                      </li>
                                      <li
                                        onClick={(e) =>
                                          updateSchedule(e, schedule, "Delete")
                                        }
                                      >
                                        Delete
                                      </li>
                                    </ul>
                                  </div>
                                )}
                              {menuOpen === index &&
                                (schedule?.state === "Rejected" ||
                                  schedule?.state === "Expired" ||
                                  schedule?.state === "Empty" ||
                                  schedule?.state === "Experimental" || schedule?.state === "Generating")
                                && (
                                  <div
                                    className="three-dot-menu-popup"
                                    ref={menuRef}
                                  >
                                    <ul>
                                      <li
                                        onClick={(e) =>
                                          updateSchedule(e, schedule, "Delete")
                                        }
                                      >
                                        Delete
                                      </li>
                                    </ul>
                                  </div>
                                )}
                            </div>
                          ) : (
                            <div className="invisible-placeholder"></div>
                          )}
                        </div>
                      </div>
                    )}
                  </Fragment>
                );
              })}
            </div>
          </div>
        </Fragment>
      ) : (
        <div className="empty-schedule-container">
          <EmptyListIcon />
          <div className="empty-schedule-text-container">
            <div className="empty-schedule-text-style empty-schedule-text">
              No Available Schedule
            </div>
            <div></div>
          </div>
          <button
            className="generate-schedule-button"
            onClick={handleClickGenerate}
          >
            <span className="generate-schedule-button-text">Generate</span>
          </button>
        </div>
      )}
    </div>
  );
};

export default ScheduleListView;
